import {
  pluginView,
  type ActionOutput,
  type OsFamily,
  type PluginSpec,
  type PluginView,
} from '@occ/contracts';
import { del, get, put, post } from '../api/http';
import { ROUTES } from '../api/routes';
import { runAction } from './actions';

/**
 * Plugins (Módulo 6.1): biblioteca de scripts reutilizables y parametrizados,
 * habilitable por cliente. Ver `packages/contracts/src/plugins.ts` para el
 * porqué del diseño (parámetros como variables de entorno, no como
 * sustitución de texto).
 */

const asArray = (payload: unknown): unknown[] => (Array.isArray(payload) ? payload : []);
const parseList = (payload: unknown): PluginView[] =>
  asArray(payload).map((row) => pluginView.parse(row));

export const listPlugins = async (): Promise<PluginView[]> => parseList(await get(ROUTES.plugins));

export const getPlugin = async (id: string): Promise<PluginView> =>
  pluginView.parse(await get(ROUTES.plugin(id)));

export const createPlugin = async (spec: PluginSpec): Promise<PluginView> =>
  pluginView.parse(await post(ROUTES.plugins, spec));

export const updatePlugin = async (id: string, spec: PluginSpec): Promise<PluginView> =>
  pluginView.parse(await put(ROUTES.plugin(id), spec));

export const deletePlugin = (id: string): Promise<void> => del(ROUTES.plugin(id));

const parseEnablements = (payload: unknown): Array<{ clientId: string; enabled: boolean }> =>
  asArray(payload)
    .filter(
      (row): row is { clientId: unknown; enabled: unknown } =>
        typeof row === 'object' && row !== null,
    )
    .map((row) => ({ clientId: String(row.clientId), enabled: Boolean(row.enabled) }));

export const listPluginEnablements = async (
  id: string,
): Promise<Array<{ clientId: string; enabled: boolean }>> =>
  parseEnablements(await get(ROUTES.pluginEnablements(id)));

export const setPluginEnablement = (
  id: string,
  clientId: string,
  enabled: boolean,
): Promise<{ pluginId: string; clientId: string; enabled: boolean }> =>
  put(ROUTES.pluginEnablement(id, clientId), { enabled });

/** Plugins que se le pueden ofrecer a un nodo: habilitados para su cliente y compatibles con su SO. */
export const runnablePlugins = async (
  clientId: string,
  osFamily: OsFamily | null,
): Promise<PluginView[]> =>
  parseList(
    await get(ROUTES.pluginsRunnable, undefined, {
      clientId,
      osFamily: osFamily ?? undefined,
    }),
  );

/**
 * Corre un plugin ya resuelto contra un nodo. Va por `script.run` del
 * catálogo con `libraryId` — la misma puerta, el mismo RBAC y la misma
 * bitácora que un script inline, ver `packages/contracts/src/actions.ts`.
 */
export const runPlugin = (
  nodeId: string,
  pluginId: string,
  args: Record<string, string>,
  timeoutSeconds: number,
): Promise<ActionOutput<'script.run'>> =>
  runAction('script.run', {
    node: { nodeId },
    script: { libraryId: pluginId, args },
    timeoutSeconds,
  });
